import { View, Text } from 'react-native';
import { ChevronRight, ChevronDown, ExternalLink } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import AnimatedPressable from '@/components/AnimatedPressable';

/**
 * A single settings row.
 *
 * `onPress` is OPTIONAL. Some rows (e.g. Team Management when it is locked
 * on native, where commerce is web-only and `openPlans` is null) have
 * nothing to do when tapped. Passing no `onPress` renders the row as a
 * plain, non-interactive `View` instead of an `AnimatedPressable`: no press
 * feedback, no trailing chevron/external-link glyph, and no `button`/`link`
 * accessibility role — VoiceOver reads it as informational text, not a
 * control. A row that LOOKS tappable but does nothing on tap is exactly the
 * kind of "obvious technical problem" App Review Guideline 2.1 flags.
 *
 * The icon/label/badge/description/value block is written out TWICE below
 * (once per branch) rather than hoisted into one shared JSX variable and
 * referenced from both. A shared `content` fragment looks tempting, but
 * `scripts/ci/check-text-containment.ts` scans SOURCE TEXT for a `<Text>`
 * with a `flex-row` ancestor within 3 literal tag levels — it doesn't
 * resolve a `{sharedVar}` reference back to where it's rendered, so hoisting
 * would silently drop these Texts out of the scanner's view (their nearest
 * *literal* ancestor becomes the non-flex-row `<View className="flex-1">`)
 * without fixing anything real. Duplicating keeps the scanner's view
 * accurate; `numberOfLines` below is what actually keeps these rows safe
 * from overflow, per `<layout_containment>` in CLAUDE.md.
 */
export function SettingsItem({
  icon,
  label,
  description,
  value,
  badge,
  onPress,
  danger = false,
  external = false,
  expanded = false,
}: {
  icon: React.ReactNode;
  label: string;
  description?: string;
  value?: string;
  badge?: string;
  onPress?: () => void;
  danger?: boolean;
  external?: boolean;
  expanded?: boolean;
}) {
  const colors = useThemeColors();
  const accessibilityLabel = `${label}${description ? `, ${description}` : ''}${value ? `, ${value}` : ''}`;

  if (!onPress) {
    return (
      <View
        accessible
        accessibilityRole="text"
        accessibilityLabel={accessibilityLabel}
        className="flex-row items-center px-4 py-4 border-b"
        style={{ borderColor: colors.border.light }}
      >
        <View
          className="w-10 h-10 rounded-xl items-center justify-center mr-3"
          style={{ backgroundColor: danger ? `${colors.error}15` : colors.primary[50] }}
        >
          {icon}
        </View>
        <View className="flex-1">
          <View className="flex-row items-center gap-2">
            <Text
              style={{ color: danger ? colors.error : colors.text.primary }}
              className="font-medium"
              numberOfLines={1}
            >
              {label}
            </Text>
            {badge && (
              <View className="px-2 py-0.5 rounded-full" style={{ backgroundColor: colors.primary[100] }}>
                <Text style={{ color: colors.primary[700] }} className="text-xs font-semibold" numberOfLines={1}>
                  {badge}
                </Text>
              </View>
            )}
          </View>
          {description && (
            <Text style={{ color: colors.text.tertiary }} className="text-sm mt-0.5" numberOfLines={2}>
              {description}
            </Text>
          )}
        </View>
        {value && (
          <Text style={{ color: colors.text.secondary }} className="text-sm mr-2" numberOfLines={1}>
            {value}
          </Text>
        )}
      </View>
    );
  }

  return (
    <AnimatedPressable
      onPress={onPress}
      accessibilityRole={external ? 'link' : 'button'}
      accessibilityLabel={accessibilityLabel}
      className="flex-row items-center px-4 py-4 border-b"
      style={{ borderColor: colors.border.light }}
    >
      <View
        className="w-10 h-10 rounded-xl items-center justify-center mr-3"
        style={{ backgroundColor: danger ? `${colors.error}15` : colors.primary[50] }}
      >
        {icon}
      </View>
      <View className="flex-1">
        <View className="flex-row items-center gap-2">
          <Text
            style={{ color: danger ? colors.error : colors.text.primary }}
            className="font-medium"
            numberOfLines={1}
          >
            {label}
          </Text>
          {badge && (
            <View className="px-2 py-0.5 rounded-full" style={{ backgroundColor: colors.primary[100] }}>
              <Text style={{ color: colors.primary[700] }} className="text-xs font-semibold" numberOfLines={1}>
                {badge}
              </Text>
            </View>
          )}
        </View>
        {description && (
          <Text style={{ color: colors.text.tertiary }} className="text-sm mt-0.5" numberOfLines={2}>
            {description}
          </Text>
        )}
      </View>
      {value && (
        <Text style={{ color: colors.text.secondary }} className="text-sm mr-2" numberOfLines={1}>
          {value}
        </Text>
      )}
      {external ? (
        <ExternalLink size={16} color={colors.text.tertiary} />
      ) : expanded ? (
        <ChevronDown size={20} color={colors.primary[600]} />
      ) : (
        <ChevronRight size={20} color={colors.text.tertiary} />
      )}
    </AnimatedPressable>
  );
}
